import { Grid } from '../../core/Grid.js';
import type { Point } from '../../core/GridGeometry.js';
import { DelugeGame } from './DelugeGame.js';
import { GridRenderer } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS, colorWithAlpha, canvasFont } from '../../rendering/Renderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { getSections, getQuadrants, getSectionContaining, Quadrant, SectionId } from './DelugeUtils.js';
import type { DiagonalWallType } from './DelugeUtils.js';
import { TweenScheduler, MOTION, staggerDelay } from '../../rendering/juice.js';

export interface DelugeRenderConfig extends Partial<RenderConfig> {
    wallColor?: string;
    wallWidth?: number;
    waterColor?: string;
    faucetColor?: string;
    fillTimeColor?: string;
    warningColor?: string;
}

const DELUGE_DEFAULTS = {
    wallColor: COLORS.teal,
    wallWidth: 4,
    waterColor: colorWithAlpha('blue', 0.4),
    faucetColor: COLORS.blue,
    fillTimeColor: COLORS.amber,
    warningColor: COLORS.amber
};

// ── Wall animation (docs/juice.md) ────────────────────────────────────
//
// A cell can hold *both* diagonals, so they are tracked as two independent
// pieces rather than one object with three orientations — which is what the
// cycle none → / → \ → X → none actually does: add one, swap it, add the other,
// clear both. Each is stroked on and off exactly like a Mirrors mirror, from the
// same canonical end: `/` from its bottom-left, `\` from its top-left.
//
// The water is *not* animated. It is simulation, and this first pass leaves it
// alone — see docs/juice.md on `replay`.

/** A cell's diagonal, keyed `row,col:f` or `row,col:b`. */
type DiagonalKey = string;

interface DiagonalTween {
    kind: 'enter' | 'exit';
    /** Clock time the tween begins — in the future while a stagger delay runs. */
    start: number;
    /** Draw from the far corner instead of the canonical one — see `diagonalReversed`. */
    reversed: boolean;
    /** Exit only: the colour it had on the last frame it existed. */
    color: string;
}

/**
 * A diagonal's two corners as grid points, in canonical stroke order: `/` runs
 * bottom-left → top-right, `\` runs top-left → bottom-right.
 */
function diagonalEnds(key: DiagonalKey): [Point, Point] {
    const [cellKey, which] = key.split(':');
    const [row, col] = cellKey.split(',').map(Number);
    return which === 'f'
        ? [{ row: row + 1, col }, { row, col: col + 1 }]
        : [{ row, col }, { row: row + 1, col: col + 1 }];
}

/** Every diagonal a wall type puts on the board. */
function diagonalsOf(cellKey: string, type: DiagonalWallType): DiagonalKey[] {
    if (type === 'f') return [`${cellKey}:f`];
    if (type === 'b') return [`${cellKey}:b`];
    if (type === 'x') return [`${cellKey}:f`, `${cellKey}:b`];
    return [];
}

export class DelugeRenderer extends GridRenderer {
    private delugeConfig: typeof DELUGE_DEFAULTS;
    private gateMode: boolean = false;

    // Animation state, all renderer-local (docs/juice.md)
    private tweens = new TweenScheduler();
    private diagTweens = new Map<DiagonalKey, DiagonalTween>();
    private diagState = new Set<DiagonalKey>();          // what the last frame drew
    private diagColors = new Map<DiagonalKey, string>(); // last colour, for removals
    private diagsSynced: boolean = false;                // first frame seeds
    private traceAnchor: Point | null = null;            // the corner a keyboard trace is on
    private faucetCol: number | null = null;             // where the faucet is now
    private faucetEnter: number | null = null;           // it growing in there
    private faucetExit: { col: number; start: number } | null = null;  // the one it left
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating: boolean = false;
    private lastGame: DelugeGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: DelugeRenderConfig = {}) {
        super(canvas, grid, config);
        this.delugeConfig = { ...DELUGE_DEFAULTS, ...config };
        // The water fill is a solid wash of blue — the cursor needs a backing stroke
        // to stay readable over it.
        this.keyboardCursorHalo = true;
        // The top rim is the faucet row: show a faucet-style marker there.
        this.keyboardFineTopRimSlots = true;
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Disable tweening (see `TweenScheduler`). Drops anything in flight, so the
     *  next render draws every wall and the faucet at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (enabled) return;
        this.diagTweens.clear();
        this.faucetEnter = null;
        this.faucetExit = null;
    }

    render(game: DelugeGame): void {
        const grid = game.getGrid();

        this.lastGame = game;
        this.animating = false;
        this.syncDiagonalTweens(game.getDiagonalWalls(), game.getTraceAnchor());
        this.syncFaucetTween(game.getFaucetCol());

        this.clear();
        this.drawGrid();
        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }
        this.drawReachableSections(game);
        this.drawWater(game);
        this.drawFillTimes(game);
        this.drawDiagonalWalls(game);
        this.drawOuterWall();
        this.drawDropHints(game);
        if (isVortexGame(game)) { this.drawActiveVortexGates(game); }
        this.drawFaucet(game);
        this.drawDigitsAndBadges(grid, game);
        this.drawSnapTargets(game);
        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    // ── Wall / faucet animation (docs/juice.md) ───────────────────────

    /**
     * Diff the diagonals against the last frame and start a tween per change. A
     * bulk change (Reset, Restore best) fans out in reading order.
     */
    private syncDiagonalTweens(walls: Map<string, DiagonalWallType>, anchor: Point | null): void {
        this.traceAnchor = anchor;
        const next = new Set<DiagonalKey>();
        for (const [cellKey, type] of walls) {
            for (const key of diagonalsOf(cellKey, type)) next.add(key);
        }

        if (!this.tweens.isEnabled()) {
            this.diagTweens.clear();
            this.diagState = next;
            return;
        }
        // First frame on this board: adopt it as-is, or opening a grid with a saved
        // solution would rebuild the whole barrier.
        if (!this.diagsSynced) {
            this.diagState = next;
            this.diagsSynced = true;
            return;
        }

        const changed: DiagonalKey[] = [];
        for (const key of next) if (!this.diagState.has(key)) changed.push(key);
        for (const key of this.diagState) if (!next.has(key)) changed.push(key);
        if (changed.length === 0) return;

        const rank = (key: string) => {
            const [row, col] = key.split(':')[0].split(',').map(Number);
            return row * this.grid.size + col;
        };
        changed.sort((a, b) => rank(a) - rank(b));

        const now = this.tweens.now();
        changed.forEach((key, i) => {
            const start = now + staggerDelay(i, changed.length);
            const entering = next.has(key);
            const reversed = this.diagonalReversed(key, anchor, entering);
            if (entering) {
                this.diagTweens.set(key, { kind: 'enter', start, reversed, color: '' });
            } else {
                this.diagTweens.set(key, {
                    kind: 'exit', start, reversed,
                    color: this.diagColors.get(key) ?? COLORS.teal
                });
            }
        });

        this.diagState = next;
    }

    /**
     * Whether to draw a diagonal from its far corner rather than its canonical one.
     *
     * A keyboard trace lays a diagonal by moving between its two corners, so half
     * the time the gesture runs against the canonical stroke and the wall appears
     * to draw itself backwards. The trace anchor says which corner the cursor is
     * on — it re-anchors to wherever the hop landed — so the motion can follow the
     * hand: growing *away* from the corner it came from, and retracting back *into*
     * the corner it is on. Off a trace there is no gesture to follow and the
     * canonical end stands, the same one a Mirrors mirror is stroked from.
     */
    private diagonalReversed(key: DiagonalKey, anchor: Point | null, entering: boolean): boolean {
        if (!anchor) return false;
        const [a, b] = diagonalEnds(key);
        const atA = anchor.row === a.row && anchor.col === a.col;
        const atB = anchor.row === b.row && anchor.col === b.col;
        if (!atA && !atB) return false;
        return entering ? atA : atB;
    }

    /** How much of a diagonal is on the board this frame. */
    private diagExtent(tween: DiagonalTween, now: number): { grown: number; done: boolean } {
        const { value, done } = this.tweens.tween(tween.start, MOTION[tween.kind], now);
        // The `exit` curve runs the *disappearance*, so invert it.
        return { grown: tween.kind === 'enter' ? value : 1 - value, done };
    }

    /**
     * Moving the faucet is a placement, not a journey: it is pulled up at the old
     * column as it is planted at the new one, both at once, with the shorter `exit`
     * keeping the overlap brief.
     */
    private syncFaucetTween(col: number): void {
        if (col === this.faucetCol) return;
        if (this.faucetCol === null || !this.tweens.isEnabled()) {
            this.faucetEnter = null;
            this.faucetExit = null;
        } else {
            const now = this.tweens.now();
            this.faucetExit = { col: this.faucetCol, start: now };
            this.faucetEnter = now;
        }
        this.faucetCol = col;
    }

    /**
     * The intersections the keyboard trace can snap a diagonal to, as small amber
     * dots. Amber rather than the usual cursor blue on purpose: everything else in the
     * keyboard language is blue, so a blue dot here would read as a second cursor.
     */
    private drawSnapTargets(game: DelugeGame): void {
        // Only while the gesture is actually live — the anchor is cleared on release,
        // but this also covers every other way out (`X`, a game switch, `K` off).
        if (!this.isKeyboardFineTracing()) return;
        const targets = game.getSnapTargets();
        if (targets.length === 0) return;

        const { padding, cellSize } = this.config;
        this.ctx.save();
        this.ctx.fillStyle = COLORS.amber;
        for (const p of targets) {
            this.ctx.beginPath();
            this.ctx.arc(padding + p.col * cellSize, padding + p.row * cellSize, 4, 0, Math.PI * 2);
            this.ctx.fill();
        }
        this.ctx.restore();
    }

    /** Gray wall around the grid rim, matching the player wall thickness. */
    private drawOuterWall(): void {
        const { padding, cellSize } = this.config;
        const rim = padding + this.grid.size * cellSize;

        this.ctx.strokeStyle = COLORS.gray;
        this.ctx.lineWidth = this.delugeConfig.wallWidth;
        this.ctx.lineJoin = 'round';
        this.ctx.lineCap = 'round';
        this.ctx.beginPath();
        this.ctx.rect(padding, padding, rim - padding, rim - padding);
        this.ctx.stroke();
    }

    /**
     * Draw amber spill hints: a quarter-circle arrow at each cell where water tips
     * over an edge during the simulation, curving from the incoming horizontal side
     * down toward the fall. Mirrors the Flood hint.
     */
    private drawDropHints(game: DelugeGame): void {
        const { padding, cellSize } = this.config;
        const hints = game.getDropHints();
        if (hints.length === 0) return;

        const r = cellSize * 0.3;
        this.ctx.save();
        this.ctx.strokeStyle = COLORS.amber;
        this.ctx.fillStyle = COLORS.amber;
        this.ctx.lineWidth = 3;
        this.ctx.lineCap = 'round';
        this.ctx.setLineDash([]);

        for (const hint of hints) {
            const x0 = padding + hint.col * cellSize;
            const y0 = padding + hint.row * cellSize;

            if (hint.from === null) {
                // Straight-down spill (no clear horizontal source).
                const cx = x0 + cellSize / 2;
                this.ctx.beginPath();
                this.ctx.moveTo(cx, y0 + cellSize * 0.18);
                this.ctx.lineTo(cx, y0 + cellSize * 0.78);
                this.ctx.stroke();
                this.drawDropArrowHead(cx, y0 + cellSize * 0.78, 0, 1);
                continue;
            }

            // Quarter pipe centered on the bottom corner of the incoming side, so
            // the arc meets the side edge horizontally and the bottom edge
            // vertically. It bulges toward the cell center; the opening faces the
            // incoming corner (away from center). The whole glyph (arc + head) is
            // shifted a touch horizontally toward the incoming corner.
            const shift = cellSize * 0.07;
            const dir = hint.from === 'W' ? -1 : 1;
            const cornerX = (hint.from === 'W' ? x0 : x0 + cellSize) + dir * shift;
            const cornerY = y0 + cellSize;
            const aEntry = -Math.PI / 2;                    // side edge (north of corner)
            const aExit = hint.from === 'W' ? 0 : Math.PI;  // bottom edge
            this.ctx.beginPath();
            this.ctx.arc(cornerX, cornerY, r, aEntry, aExit, hint.from === 'E');
            this.ctx.stroke();

            // Arrowhead at the bottom endpoint, pointing down, nudged a hair away
            // from the arc center so the tip sits just outside the sweep.
            const nudge = cellSize * 0.008;
            const exitX = hint.from === 'W' ? cornerX + r - nudge : cornerX - r + nudge;
            this.drawDropArrowHead(exitX, cornerY, 0, 1);
        }

        this.ctx.restore();
    }

    /** Filled triangle arrowhead at (x, y) pointing along unit vector (ux, uy). */
    private drawDropArrowHead(x: number, y: number, ux: number, uy: number): void {
        const { cellSize } = this.config;
        const len = cellSize * 0.15;
        const wid = cellSize * 0.065;
        const perpx = -uy, perpy = ux;
        const tipX = x + ux * len * 0.6, tipY = y + uy * len * 0.6;
        const backX = x - ux * len * 0.4, backY = y - uy * len * 0.4;

        this.ctx.beginPath();
        this.ctx.moveTo(tipX, tipY);
        this.ctx.lineTo(backX + perpx * wid, backY + perpy * wid);
        this.ctx.lineTo(backX - perpx * wid, backY - perpy * wid);
        this.ctx.closePath();
        this.ctx.fill();
    }

    private drawReachableSections(game: DelugeGame): void {
        // Only draw when there are unreachable sections (to show what IS reachable)
        if (!game.getHasUnreachableSections()) return;

        const reachable = game.getReachableSections();
        const { padding, cellSize } = this.config;

        // Draw reachable sections in amber with low alpha
        for (const sk of reachable) {
            const parts = sk.split(',');
            const row = parseInt(parts[0]);
            const col = parseInt(parts[1]);
            const sectionId = parts[2] as SectionId;

            const x = padding + col * cellSize;
            const y = padding + row * cellSize;
            const cx = x + cellSize / 2;
            const cy = y + cellSize / 2;

            this.ctx.fillStyle = colorWithAlpha('amber', 0.25);
            const quadrants = getQuadrants(sectionId);
            for (const quadrant of quadrants) {
                this.drawQuadrantTriangle(cx, cy, cellSize, quadrant);
            }
        }
    }

    private drawWater(game: DelugeGame): void {
        // Find max fill time for alpha scaling
        let maxFillTime = 4 * this.grid.size * this.grid.size; // 4 sections per cell, full grid

        // Draw water for each cell's sections
        for (let row = 0; row < this.grid.size; row++) {
            for (let col = 0; col < this.grid.size; col++) {
                const wallType = game.getWallType(row, col);
                const sections = getSections(wallType);

                for (const section of sections) {
                    const fillTime = game.getSectionFillTime(row, col, section);
                    if (fillTime !== null) {
                        // Earlier fills are more opaque, later fills more transparent
                        const alpha = maxFillTime > 0
                            ? Math.max(0, 0.3 - (fillTime / maxFillTime) * 0.3)
                            : 0.3;

                        this.drawSectionWater(row, col, section, alpha);
                    }
                }
            }
        }
    }

    private drawSectionWater(row: number, col: number, section: SectionId, alpha: number): void {
        const { padding, cellSize } = this.config;
        const x = padding + col * cellSize;
        const y = padding + row * cellSize;
        const cx = x + cellSize / 2;
        const cy = y + cellSize / 2;

        this.ctx.fillStyle = colorWithAlpha('blue', alpha);

        const quadrants = getQuadrants(section);
        for (const quadrant of quadrants) {
            this.drawQuadrantTriangle(cx, cy, cellSize, quadrant);
        }
    }

    private drawQuadrantTriangle(cx: number, cy: number, cellSize: number, quadrant: Quadrant): void {
        const halfSize = cellSize / 2;

        this.ctx.beginPath();
        this.ctx.moveTo(cx, cy);

        switch (quadrant) {
            case 'u':
                this.ctx.lineTo(cx - halfSize, cy - halfSize);
                this.ctx.lineTo(cx + halfSize, cy - halfSize);
                break;
            case 'd':
                this.ctx.lineTo(cx - halfSize, cy + halfSize);
                this.ctx.lineTo(cx + halfSize, cy + halfSize);
                break;
            case 'l':
                this.ctx.lineTo(cx - halfSize, cy - halfSize);
                this.ctx.lineTo(cx - halfSize, cy + halfSize);
                break;
            case 'r':
                this.ctx.lineTo(cx + halfSize, cy - halfSize);
                this.ctx.lineTo(cx + halfSize, cy + halfSize);
                break;
        }

        this.ctx.closePath();
        this.ctx.fill();
    }

    private drawFillTimes(game: DelugeGame): void {
        this.ctx.font = canvasFont(10);
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillStyle = this.delugeConfig.fillTimeColor;

        // Get digit positions to avoid drawing fill times on them
        const digitPositions = new Set<string>();
        const positions = game.getGrid().getAllDigitPositions();
        for (const [, pos] of positions) {
            digitPositions.add(`${pos.row},${pos.col}`);
        }

        for (let row = 0; row < this.grid.size; row++) {
            for (let col = 0; col < this.grid.size; col++) {
                // Skip digit cells and faucet cell
                if (digitPositions.has(`${row},${col}`)) continue;
                if (game.getFaucetCol() === col && row === 0) continue;

                const wallType = game.getWallType(row, col);
                const sections = getSections(wallType);

                for (const section of sections) {
                    const fillTime = game.getSectionFillTime(row, col, section);
                    if (fillTime === null) continue;

                    if (this.shouldShowFillTime(row, col, section, fillTime, game)) {
                        const pos = this.getFillTimePosition(row, col, section);
                        this.ctx.fillText(fillTime.toString(), pos.x, pos.y);
                    }
                }
            }
        }
    }

    private shouldShowFillTime(
        row: number, col: number, section: SectionId,
        fillTime: number, game: DelugeGame
    ): boolean {
        const quadrants = getQuadrants(section);

        // If section doesn't contain 'l', it's isolated from left → always show
        if (!quadrants.includes('l')) {
            return true;
        }

        // Section contains 'l', check left neighbor
        if (col === 0) {
            return true; // No left neighbor, this is the start
        }

        // Get left cell's section containing 'r'
        const leftWallType = game.getWallType(row, col - 1);
        const leftSection = getSectionContaining('r', leftWallType);
        const leftFillTime = game.getSectionFillTime(row, col - 1, leftSection);

        // Show if fill times differ (new region)
        return fillTime !== leftFillTime;
    }

    private getFillTimePosition(
        row: number, col: number, section: SectionId
    ): { x: number; y: number } {
        const { padding, cellSize } = this.config;
        const quadrants = getQuadrants(section);

        let x = padding + col * cellSize + cellSize / 2;
        let y = padding + row * cellSize + cellSize / 2; // default: center

        const hasU = quadrants.includes('u');
        const hasD = quadrants.includes('d');
        const hasL = quadrants.includes('l');
        const hasR = quadrants.includes('r');

        // Vertical position
        if (hasU && !hasD) {
            y = padding + row * cellSize + cellSize / 5;
        } else if (hasD && !hasU) {
            y = padding + row * cellSize + cellSize * 4 / 5;
        }

        // Horizontal offset for side-only sections (cross wall)
        if (!hasU && !hasD) {
            if (hasL && !hasR) {
                x = padding + col * cellSize + cellSize / 5;
            } else if (hasR && !hasL) {
                x = padding + col * cellSize + cellSize * 4 / 5;
            }
        }

        // Vertical offset for no-wall sections (all quadrants)
        if (hasU && hasD && hasL && hasR) {
            y = padding + row * cellSize + cellSize / 5;
        }

        return { x, y };
    }

    /** Scoring overlay: redraw the diagonal walls (primary) in amber. */
    protected drawScoringPrimary(game: DelugeGame): void {
        this.drawDiagonalWalls(game, COLORS.amber);
    }

    private drawDiagonalWalls(game: DelugeGame, colorOverride?: string): void {
        const { wallWidth } = this.delugeConfig;
        const disconnected = game.getDisconnectedWalls();
        const walls = game.getDiagonalWalls();

        // The scoring overlay redraws the walls in a flat colour on a frozen board:
        // it reads state, it doesn't advance it. (The diff already ran, in render.)
        const live = colorOverride === undefined;
        const now = this.tweens.now();

        this.ctx.lineWidth = wallWidth;
        this.ctx.lineCap = 'round';

        const onBoard = new Set<DiagonalKey>();
        for (const [cellKey, wallType] of walls) {
            if (wallType === null) continue;
            const color = colorOverride ?? (disconnected.has(cellKey) ? COLORS.amber : COLORS.teal);

            for (const key of diagonalsOf(cellKey, wallType)) {
                onBoard.add(key);
                if (live) this.diagColors.set(key, color);

                const tween = live ? this.diagTweens.get(key) : undefined;
                let grown = 1;
                let reversed = false;
                if (tween) {
                    const shape = this.diagExtent(tween, now);
                    if (shape.done) this.diagTweens.delete(key);
                    else {
                        this.animating = true;
                        grown = shape.grown;
                        reversed = tween.reversed;
                    }
                }
                this.ctx.strokeStyle = color;
                this.strokeDiagonal(key, grown, reversed);
            }
        }

        // Diagonals being removed are already gone from game state — they only exist
        // here, un-drawing in the colour they wore when they went.
        for (const [key, tween] of live ? this.diagTweens : []) {
            if (tween.kind !== 'exit' || onBoard.has(key)) continue;
            const shape = this.diagExtent(tween, now);
            if (shape.done) {
                this.diagTweens.delete(key);
                this.diagColors.delete(key);
                continue;
            }
            this.animating = true;
            this.ctx.strokeStyle = tween.color;
            this.strokeDiagonal(key, shape.grown, tween.reversed);
        }
    }

    /**
     * Draw the `grown` fraction of one diagonal, from its canonical starting end —
     * the same ends a Mirrors mirror is stroked from, so the two games' diagonals
     * are drawn the same way round.
     */
    private strokeDiagonal(key: DiagonalKey, grown: number, reversed: boolean = false): void {
        if (grown <= 0) return;
        const { padding, cellSize } = this.config;
        const ends = diagonalEnds(key);
        const [from, to] = reversed ? [ends[1], ends[0]] : ends;

        const ax = padding + from.col * cellSize, ay = padding + from.row * cellSize;
        const bx = padding + to.col * cellSize, by = padding + to.row * cellSize;

        this.ctx.beginPath();
        this.ctx.moveTo(ax, ay);
        this.ctx.lineTo(ax + (bx - ax) * grown, ay + (by - ay) * grown);
        this.ctx.stroke();
    }

    private drawFaucet(game: DelugeGame): void {
        const now = this.tweens.now();
        const color = game.checkWin() ? COLORS.teal : this.delugeConfig.faucetColor;

        // The faucet left behind, shrinking away at its old column.
        if (this.faucetExit) {
            const { value, done } = this.tweens.tween(this.faucetExit.start, MOTION.exit, now);
            if (done) this.faucetExit = null;
            else {
                this.animating = true;
                this.drawFaucetAt(this.faucetExit.col, color, 1 - value);
            }
        }

        let scale = 1;
        if (this.faucetEnter !== null) {
            const { value, done } = this.tweens.tween(this.faucetEnter, MOTION.enter, now);
            if (done) this.faucetEnter = null;
            else {
                this.animating = true;
                scale = value;
            }
        }
        this.drawFaucetAt(game.getFaucetCol(), color, scale);
    }

    /** The faucet on the top rim, at `scale` of its full size about the rim point. */
    private drawFaucetAt(faucetCol: number, color: string, scale: number): void {
        if (scale <= 0.01) return;
        const { padding, cellSize } = this.config;
        const x = padding + faucetCol * cellSize + cellSize / 2;

        this.ctx.save();
        // Scale about the point on the rim the faucet sits at, so it grows out of
        // the wall rather than drifting along it.
        this.ctx.translate(x, padding);
        this.ctx.scale(scale, scale);
        this.ctx.translate(-x, -padding);

        // Break a background wedge through the rim wall so the faucet reads as an
        // opening into it.
        this.ctx.fillStyle = this.config.backgroundColor;
        this.ctx.beginPath();
        this.ctx.moveTo(x - 8, padding - 2);
        this.ctx.lineTo(x + 8, padding - 2);
        this.ctx.lineTo(x, padding + 12);
        this.ctx.closePath();
        this.ctx.fill();

        this.drawRimMarker({
            position: { row: 0, col: faucetCol },
            color,
            entry: true,
            withDrop: true
        });
        this.ctx.restore();
    }

    private drawDigitsAndBadges(grid: Grid, game: DelugeGame): void {
        this.drawDigits(grid);

        // Draw fill time badges. Digits filled in order are teal; any that fill out
        // of order are amber — a hint, not an alarm, so no red here (red is kept for
        // the structural wall errors in the status bar).
        const correctDigits = new Set(game.getCorrectDigits());
        for (const [digit, pos] of grid.getAllDigitPositions()) {
            const fillTime = game.getCellFillTime(pos.row, pos.col);
            if (fillTime !== null) {
                const badgeColor = correctDigits.has(digit) ? COLORS.teal : this.delugeConfig.warningColor;
                this.drawBadge(pos, 'NE', fillTime.toString(), badgeColor, 'lg', true);
            }
        }
    }

}
